import { ref, computed } from 'vue';
import { defineStore } from 'pinia';
import axios from 'axios';
import { useRoute, useRouter } from 'vue-router';
import { useErrorOverlay } from './overlays/error-overlay';
import { useAsyncState } from '@vueuse/core';

export type User = {
  id: number;
  username: string;
  email: string;
  is_active: boolean;
  is_staff: boolean;
  is_superuser: boolean;
  config: {
    authorized_bnum: string;
    expired: boolean;
    expire_datetime: string;
    expire_policy: string;
    guest: boolean;
    belonging_to: number[];
  };
};

export const useUserStore = defineStore("user", () => {
  const error_overlay = useErrorOverlay();
  const router = useRouter();
  const route = useRoute();
  /** State **/
  const user = ref<User | null>(null);
  const authorized_stations = ref<Array<number> | null>([]);

  /** Getters **/
  const logged = computed((): boolean => !!user.value);
  const id = computed((): number | null => user.value ? user.value.id : null);
  const authorized_on = computed(() => {
    return (qrcode: number): boolean => (authorized_stations.value ?? []).includes(qrcode)
  });
  const is_guest = computed<boolean>(() => user.value ? user.value.config.guest : false);

  /** Actions **/
  async function fetch(): Promise<void> {
      console.log(`[user.fetch]: fetching user`);
      try {
        const u_response = await axios.get<User>("/v3/users/myself");
        const myself : User = u_response.data;
        const p_response = await axios.get<{ authorized_qrcode: Array<number> }>(`/v3/users/${myself.id}/charge_permissions/`);
        const permissions : Array<number> = p_response.data.authorized_qrcode;
        user.value = myself;
        authorized_stations.value = permissions;
        console.log(`[user.fetch] user fetch completed`);
      } catch (response: any) {
        const err = response.response;
        if (!err) {
          // Network error: no HTTP response was received (DNS failure, CORS, connection refused, etc.)
          console.log("[user.fetch]: network error (no response)", response);
          error_overlay.show_timeout();
          return;
        }
        console.log(`[user.fetch] error ${err.status}: `, err);
        switch (err.status) {
          case 408:
            console.log("[user.fetch] fetch timeout");
            error_overlay.show_timeout();
            break;
          case 401:
          case 403:
            console.log(`[user.fetch]: fetch failed with status ${err.status}, message '${err.statusText}'`);
            if ((route ?? null) === null) {
              console.log("[user.fetch]: route is null - first initialization");
              await router.push("/login");
              return
            }
            if (route.name !== "login") {
              await router.push("/login");
            }
            break;
          default:
            error_overlay.show(err.customMessage, err.status);
            break;
        }
      }
  }

  function clear() {
    user.value = null;
    authorized_stations.value = null;
    error_overlay.hide();
  }

  async function do_login(username: string, password: string, qrcodeid: number): Promise<number>
  {
    try {
      await axios.post("/v3/users/login/", { username, password, qrcodeid });
      console.log("[user.do_login]: logged in correctly");
      return 200;
    } catch (err: any) {
      const resp = err.response;
      console.log(`[user.do_login]: error ${resp.status}: ${resp.data}`);
      switch (err.status) {
        case 408:
          console.log("[user.do_login]: login request timed out");
          break;
        default:
          console.log(`[user.do_login]: login failed with status ${resp.status}, message '${resp.data}'`);
          break;
      }
      return resp.status;
    }
  }

  function login(username: string, password: string, qrcodeid: number) {
    const { state, isLoading } = useAsyncState(do_login(username, password, qrcodeid), null);
    return { state, loading: isLoading };
  }

  async function logout(): Promise<void> {
    await axios.get("/v3/users/logout/");
    clear();
    console.log("[user.logout]: logout completed");
    // NOTE: not fetching the station again!
  }

  return {
    user,
    logged, id, authorized_on, is_guest,
    fetch, clear, do_login, login, logout,
  };
});
